import { GatewayCredentials, GatewayRule, PaymentGatewayId } from '../types';

/**
 * Checks if a specific payment gateway has valid API keys / credentials configured.
 */
export function isGatewayConnected(
  gateway: PaymentGatewayId,
  credentials?: GatewayCredentials
): boolean {
  if (!credentials) return false;

  switch (gateway) {
    case 'stripe': {
      const stripe = credentials.stripe;
      return Boolean(
        (stripe?.publishableKey && stripe.publishableKey.trim() !== '') ||
        (stripe?.secretKey && stripe.secretKey.trim() !== '')
      );
    }
    case 'razorpay': {
      const razorpay = credentials.razorpay;
      return Boolean(
        (razorpay?.keyId && razorpay.keyId.trim() !== '') ||
        (razorpay?.keySecret && razorpay.keySecret.trim() !== '')
      );
    }
    case 'cashfree': {
      const cashfree = credentials.cashfree;
      return Boolean(
        (cashfree?.appId && cashfree.appId.trim() !== '') ||
        (cashfree?.secretKey && cashfree.secretKey.trim() !== '')
      );
    }
    case 'lemonsqueezy': {
      const ls = credentials.lemonsqueezy;
      return Boolean(ls?.apiKey && ls.apiKey.trim() !== '');
    }
    case 'paddle': {
      const paddle = credentials.paddle;
      return Boolean(
        (paddle?.vendorId && paddle.vendorId.trim() !== '') ||
        (paddle?.authCode && paddle.authCode.trim() !== '') ||
        (paddle?.publicKey && paddle.publicKey.trim() !== '')
      );
    }
    case 'checkoutcom': {
      const co = credentials.checkoutcom;
      return Boolean(
        (co?.publicKey && co.publicKey.trim() !== '') ||
        (co?.secretKey && co.secretKey.trim() !== '')
      );
    }
    case 'chargebee': {
      const cb = credentials.chargebee;
      return Boolean(
        (cb?.site && cb.site.trim() !== '') ||
        (cb?.publishableKey && cb.publishableKey.trim() !== '')
      );
    }
    default: {
      const genericCreds = (credentials as any)?.[gateway];
      if (!genericCreds) return false;
      return Object.values(genericCreds).some(
        (val) => typeof val === 'string' && val.trim() !== ''
      );
    }
  }
}

/**
 * Returns a list of all payment gateway IDs that have API keys connected.
 */
export function getConnectedGateways(credentials?: GatewayCredentials): PaymentGatewayId[] {
  const allGateways: PaymentGatewayId[] = [
    'stripe',
    'razorpay',
    'cashfree',
    'lemonsqueezy',
    'paddle',
    'checkoutcom',
    'chargebee',
  ];
  return allGateways.filter((gw) => isGatewayConnected(gw, credentials));
}

/**
 * Checks if a gateway is connected AND eligible based on currency & country rules.
 */
export function isGatewayAvailableForCustomer(
  gateway: PaymentGatewayId,
  credentials: GatewayCredentials | undefined,
  rules: GatewayRule[],
  currency: string,
  country: string
): boolean {
  // CRITICAL REQUIREMENT: Must have API keys connected to SubScale
  if (!isGatewayConnected(gateway, credentials)) {
    return false;
  }

  // Check if any rule allows this gateway for the currency and country
  const matchingRule = rules.find((rule) => {
    if (!rule.isEnabled || rule.gateway !== gateway) return false;
    const matchesCurrency =
      rule.currencies.includes('*') ||
      rule.currencies.includes(currency) ||
      (currency === '₹' && rule.currencies.includes('INR'));
    const matchesCountry =
      rule.countries.includes('*') || rule.countries.includes(country);
    return matchesCurrency && matchesCountry;
  });

  // If no explicit rules exist but the gateway is connected and active in rules
  if (!matchingRule) {
    const hasAnyRule = rules.some((r) => r.gateway === gateway);
    if (!hasAnyRule) {
      return true;
    }
    return false;
  }

  return true;
}

/**
 * Returns only the payment gateways that are connected with API keys AND match the customer criteria.
 */
export function getAvailableGatewaysForCustomer(
  credentials: GatewayCredentials | undefined,
  rules: GatewayRule[],
  currency: string,
  country: string
): PaymentGatewayId[] {
  const connected = getConnectedGateways(credentials);
  return connected.filter((gw) =>
    isGatewayAvailableForCustomer(gw, credentials, rules, currency, country)
  );
}
